import { useState, useCallback, useMemo } from "react";
import { FaArrowRight } from "react-icons/fa6";
import {
  AddressInputField,
  DropDownInput,
  UploadInput,
  Button,
  InputField,
} from "@/components";
import { SheetClose } from "@/components/ui/sheet.jsx";
import { useDispatch } from "react-redux";
import { createEscalation } from "../../app/slices";
import { useAlert, useConfirmationDialog, useFormData } from "@/";
import { escalationTypes, subcategoryOptions } from "../../utils/Data";

const NewEscalationForm = ({ handleCloseSidebar, setSheetOpen = () => {} }) => {
  const dispatch = useDispatch();
  const { alert, AlertDialog } = useAlert();
  const { confirm, ConfirmationModal } = useConfirmationDialog();
  const convertToFormData = useFormData();

  const [formData, setFormData] = useState({
    shippingId: "",
    escalationType: "",
    escalationSubcategory: "",
    escalationDescription: "",
    escalationScreenshot: "", // Set initial value as null for file uploads
  });

  // Filter subcategory options based on the selected escalation type
  const filteredSubcategories = useMemo(
    () =>
      formData.escalationType
        ? subcategoryOptions.filter(
            (opt) =>
              opt.category === formData.escalationType ||
              opt.category === "general"
          )
        : [],
    [formData.escalationType, subcategoryOptions]
  );

  // Generic input handler to update state dynamically
  const handleChange = useCallback((key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Reset subcategory when escalation type changes
  const handleEscalationTypeChange = (value) => {
    setFormData((prevData) => ({
      ...prevData,
      escalationType: value,
      escalationSubcategory: "", // Reset subcategory when type changes
    }));
  };
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Convert comma-separated shipping IDs into an array
      const shipmentIdsArray = formData.shippingId
        .split(",")
        .map((id) => id.trim())
        .filter((id) => id !== ""); // Remove empty values

      // Validate required fields
      if (
        !shipmentIdsArray.length ||
        !formData.escalationType ||
        !formData.escalationDescription
      ) {
        await alert("Please fill in all required fields.");
        return;
      }

      // Prepare request data
      const requestData = {
        data: {
          shipmentIds: shipmentIdsArray,
          message: formData.escalationDescription,
          type: formData.escalationType,
          subType: formData.escalationSubcategory,
        },
        evidences: formData.escalationScreenshot,
      };
      console.log("requestData:::", requestData);
      // Convert request data to FormData and handle file upload
      const formDataObj = convertToFormData(requestData);
      console.log("FormData Object::::::::::", formDataObj);

      // Create escalation
      const response = await dispatch(createEscalation(formDataObj));
      if (createEscalation.fulfilled.match(response)) {
        console.log("Escalation created successfully:", response.payload);
      } else if (createEscalation.rejected.match(response)) {
        console.error("Escalation creation failed:", response.payload);
        await alert(response.payload || "Escalation creation failed.");
        // setSheetOpen(false);
        return;
      }
      // Show success message
      await alert("Escalation Created Successfully");

      // Reset form
      setFormData({
        shippingId: "",
        escalationType: "",
        escalationSubcategory: "",
        escalationDescription: "",
        escalationScreenshot: "",
      });

      setSheetOpen(false); // Close the sheet after successful submission
    } catch (error) {
      console.error("Escalation creation failed:", error);
      await alert(
        error?.message || "Failed to create escalation. Please try again."
      );
    }
  };

  return (
    <div className="flex flex-col gap-1">
      {/* Shipping ID */}
      <InputField
        label="Shipping ID (Comma separated)"
        type="text"
        placeholder="Enter Shipping IDs separated by commas"
        value={formData.shippingId}
        onChange={(val) => handleChange("shippingId", val)}
      />

      {/* Escalation Type Selection */}
      <DropDownInput
        label="Escalation Type"
        placeholder="Select Escalation Type"
        options={escalationTypes}
        value={formData.escalationType}
        onChange={(value) => handleEscalationTypeChange(value)}
      />

      {/* Escalation Subcategory Selection */}
      <DropDownInput
        label="Escalation Subcategory"
        placeholder="Select Escalation Subcategory"
        options={filteredSubcategories}
        value={formData.escalationSubcategory}
        onChange={(value) =>
          setFormData((prev) => ({ ...prev, escalationSubcategory: value }))
        }
        disabled={!formData.escalationType}
      />

      {/* Problem Description */}
      <AddressInputField
        label="Describe your Problem"
        placeholder="Enter your problem"
        value={formData.escalationDescription}
        onChange={(val) => handleChange("escalationDescription", val)}
      />

      {/* Upload Screenshot */}
      <UploadInput
        label="Upload Screenshot (if any)"
        value={formData.escalationScreenshot}
        multiple={true}
        onChange={(value) => handleChange("escalationScreenshot", value)}
        note="Allowed: PNG, JPEG, PDF (max 5MB) | MP4, MOV, WebM (max 10MB)"
      />

      {/* Action Buttons */}
      <div className="flex justify-end gap-2">
        <SheetClose asChild>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCloseSidebar}
            className="mt-4"
          >
            Close
          </Button>
        </SheetClose>
        <Button
          type="submit"
          icon={FaArrowRight}
          variant="default"
          onClick={handleSubmit}
          size="sm"
          className="mt-4"
        >
          Create
        </Button>
      </div>
      {/* Alert Dialog */}
      <AlertDialog />

      {/* Confirmation Dialog
       */}
      <ConfirmationModal />
    </div>
  );
};

export default NewEscalationForm;
